import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import {
  Box,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { courseCatalogFilters } from "../filterConfigurations";
import { formatDateTime } from "../../../utils/formatDate";

const CourseCatalog = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Course Catalog"]?.edit;
  const { data: courses, loading, error } = useFirestore("TpCourseCatalog");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Course Catalog";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(courses || [], courseCatalogFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        field: "Course",
        headerName: "Course",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Subject",
        headerName: "Subject",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Lab_Type_Course",
        headerName: "Lab Type Course",
        flex: 1,
        minWidth: 150,
        valueFormatter: (value) => {
          if (value === undefined || value === null) return "";
          if (value === 0 || value === "0") return "No";
          if (value === -1 || value === "-1") return "Yes";
          return value ?? "";
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatDateTime(params.value),
        sortComparator: (v1, v2) => {
          const aDate = v1 && v1.seconds ? v1.seconds : 0;
          const bDate = v2 && v2.seconds ? v2.seconds : 0;
          return aDate - bDate;
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatDateTime(params.value),
        sortComparator: (v1, v2) => {
          const aDate = v1 && v1.seconds ? v1.seconds : 0;
          const bDate = v2 && v2.seconds ? v2.seconds : 0;
          return aDate - bDate;
        },
      },
      {
        field: "Credit_Type",
        headerName: "Credit Type",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "Credits",
        headerName: "Credits",
        flex: 1,
        minWidth: 100,
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((course, index) => ({
      id: course.id || index, // Use course.id if available, otherwise use index
      ...course,
    }));
  }, [filteredData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        {/* Display the error message from the useFirestore hook */}
        {/* The error object from useFirestore might be a string or an object with a message property */}
        <p>
          Error fetching courses:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={courseCatalogFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Course Catalog Filters"
          resultCount={filteredData?.length || 0}
          totalCount={courses?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={() => {}} />}
          {canEdit && (
            <EditUpload
              collectionName="TpCourseCatalog"
              onActionComplete={() => {}}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={courses}
              templateFields={Array.from(
                new Set([
                  ...columns.map((col) => col.field),
                  "Ode_Course",
                  "Ode_Subject_Code",
                  "TP_Pathway",
                  "Ode_Program",
                  "Ode_Program_Code",
                  "TP_Pthwy_abv",
                  "School_Year_Active",
                  "Shell_Option",
                  "Course_And_Subject",
                  "Internal_Note",
                  "External_Note",
                  "Pilot_Note",
                  "Pilot",
                  "New",
                  "credit_capture_scheme",
                ]),
              )}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="TpCourseCatalog" />
          )}
        </Box>

        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick={!canEdit}
            onRowClick={(params) => {
              if (canEdit) {
                setSelectedCourse(params.row);
                setEditDialogOpen(true);
              }
            }}
            slots={{
              toolbar: SimpleToolbar,
            }}
            slotProps={{
              toolbar: {
                fileName: "course-catalog",
              },
            }}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {selectedCourse && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          course={selectedCourse}
          onRecordUpdated={() => {
            setSnackbarMessage("Record has been updated successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setEditDialogOpen(false);
          }}
        />
      )}
    </Container>
  );
};

export default CourseCatalog;
